/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'jquery',
	'underscore',
	'../VisView',
	'text!./Kpi.html',
	'text!./kpiSparklineSpec.json',
	'../../../util/DashboardFormatter',
	'./KpiEventTarget',
	'./KpiViewFactory',
	'../VisEventHandler',
	'../../../DynamicFileLoader',
	'../../../widgets/livewidget/nls/StringResources',
	'../../../util/TextScrambler',
	'../../../features/content/dataQueryExecution/QueryResults'
], ($, _, VisView, KpiTemplate, KPISparklineSpec, Formatter, KpiEventTarget, KpiViewFactory, VisEventHandler, DynamicFileLoader, StringResources, TextScrambler, QueryResults) => {
	'use strict';

	const QUERY_RESULT_ROW_INDEX = 0;
	const BASE_DATA_ITEM_INDEX = 0;
	const TARGET_DATA_ITEM_INDEX = 1;

	const KpiView = VisView.extend({
		BASE_SLOT_ID: 'actual',
		TARGET_SLOT_ID: 'goal',
		SPARKLINE_TIME_SLOT_ID: 'sparkline.time',
		SPARKLINE_ACTUAL_SLOT_ID: 'sparkline.actual',
		className: 'dataview kpi-widget',
		templateString: KpiTemplate,

		init: function(options) {
			KpiView.inherited('init', this, arguments);
			this._baseValueView = null;
			this._targetValueView = null;
			this._sparkLineView = null;
			this._content = options.content;

			const isNewFeature = this._isNewConditionalFormatFeature();
			const condFeatureName = isNewFeature ? 'ConditionalFormatting' : 'conditionalFormatting';

			this._conditionalFormattingFeature = this._content.getFeature(condFeatureName);
			this._colorsService = this.dashboardApi.getFeature('Colors');
			this._visualizationAPI = this._content.getFeature('Visualization.internal');
			this._kpiViewFactory = options.kpiViewFactory || new KpiViewFactory();

			// deregister here since the new component do not have access to the dashboardAPI to check whether the feature flag is turned on or off
			// Instead when the new feature gets loaded it always register itself with the 'Properties' and 'CustomColor' feature
			// Remove this code when the new feature get turn on by default
			if (!isNewFeature) {
				const newCondFeature = this._content.getFeature('ConditionalFormatting');
				if (newCondFeature) {
					this._content.getFeature('Properties').deregisterProvider(newCondFeature);
					this.dashboardApi.getFeature('CustomColor').deregisterProvider(newCondFeature);
				}
			}
		},

		summaryReveal: function() {
			this.visModel.renderCompleteBeforeAnimation();
			if( !this._readyToRender()){
				return Promise.resolve();
			}
			var $label = this.$el.find('.kpi-widget-base-value .labeled-value-label');
			var $value = this.$el.find('.kpi-widget-base-value .labeled-value-value');

			if($label.length) {
				this.labelScrambler = new TextScrambler({
					el: $label[0]
				});
				this.labelScrambler.scramble();
			}

			if($value.length) {
				this.valueScrambler = new TextScrambler({
					ignoreSet: ['-', ',', '.'],
					el: $value[0]
				});
				this.valueScrambler.scramble();
			}
			return Promise.resolve();
		},

		remove: function() {
			if (this.eventHandler) {
				this.eventHandler.remove();
				this.eventHandler = null;
			}
			this._removeViews();
			KpiView.inherited('remove', this, arguments);
		},

		/**
 		 * Loads and creates the control. Returns a promise which is resolved when the control
 		 * is created and ready to render
 		 */
		whenVisControlReady: function() {
			if (this.visControl) {
				return Promise.resolve(this.visControl);
			} else {
				return DynamicFileLoader.load(['dashboard-analytics/visualizations/renderer/kpi/control/KpiControl'])
					.then((modules) => {
						const VisControl = modules[0];
						this.visControl = new VisControl({
							domNode: this.$el.find('.kpi-widget-content')[0]
						});
						this.eventHandler = new VisEventHandler({
							target: new KpiEventTarget({
								$el: this.$el,
								visControl: this.visControl,
								visAPI: this.visModel,
								view: this,
								visualization: this.visualization
							}),
							transaction: this.transactionApi,
							ownerWidget: this.ownerWidget,
							visAPI: this.visModel,
							edgeSelection: true
						});
						return this.visControl;
					});
			}
		},

		_removeViews: function() {
			if (this._baseValueView) {
				this._baseValueView.remove();
				this._baseValueView = null;
			}
			if (this._targetValueView) {
				this._targetValueView.remove();
				this._targetValueView = null;
			}
			this._removeSparkLine();
		},

		_initializeConditionalFormatting: function() { // This is temporary until features have a proper way of initializing themselves
			if (this._conditionalFormattingFeature) {
				return this._conditionalFormattingFeature.initializeFeature();
			} else {
				return Promise.resolve();
			}
		},

		render: function(renderInfo) {
			// This is temporary until features have a proper way of initializing themselves
			return this._initializeConditionalFormatting()
				.then(() => {
					this._baseValue = null;
					this._targetValue = null;
					const baseSlot = this._getBaseSlot();
					const targetSlot = this._getTargetSlot();
					if (!this._readyToRender() || !baseSlot) {
						this.renderIconView();
						return Promise.resolve();
					}

					this.removeIconView();

					$(this.contentNode).css('text-align', this._getTextAlignment() || 'center');

					if (!this._baseValueNode) {
						this._baseValueNode = this.$el.find('.kpi-widget-base-value');
						this._targetValueNode = this.$el.find('.kpi-widget-target-value');
						this._sparkLineNode = this._getSparklineContainer();
					}

					const queryResultData = renderInfo.data.getResult();
					this._baseValue = this._getQueryResultValue(queryResultData, QUERY_RESULT_ROW_INDEX, BASE_DATA_ITEM_INDEX);
					this._targetValue = this._getQueryResultValue(queryResultData, QUERY_RESULT_ROW_INDEX, TARGET_DATA_ITEM_INDEX);

					const baseProperties = this._getBaseProperties();
					const baseFormattedValue = this._formatValue(baseSlot, this._baseValue);
					const baseDataItem = this._getDataItem(baseSlot);
					const baseLabel = baseDataItem.getLabel();
					const targetProperties = this._getTargetProperties();

					this._targetValueNode.addClass('hidden');
					this._targetValueNode.empty();

					let renderPromises = [];
					const targetSlotIsMapped = this._isSlotMapped(targetSlot);
					if ((targetSlotIsMapped || ((targetProperties.manualValue || targetProperties.manualValue  === 0) && !isNaN(targetProperties.manualValue)))) {
						let formatSlot, targetLabel;
						if (targetSlotIsMapped) {
							formatSlot = targetSlot;
							const targetDataItem = this._getDataItem(targetSlot);
							targetLabel = targetDataItem.getLabel();
						} else { //Manual Value
							formatSlot = baseSlot;
							targetLabel = StringResources.get('kpiDefaultTargetLabel');
							this._targetValue = Number(targetProperties.manualValue);
						}

						if (targetProperties.showValue || targetProperties.showDistance) {
							this._targetValueNode.removeClass('hidden');
							const targetString = this._getTargetValueString(targetProperties, formatSlot);
							renderPromises.push(this._renderLabeledValue(targetString, targetLabel, targetProperties, 'targetValue'));
						}
					}

					const conditionalFormatting = (this._conditionalFormattingFeature) ? this._conditionalFormattingFeature.getFormatting(this._baseValue, this._targetValue, baseDataItem.getColumnId()) : null;
					renderPromises.push(this._renderLabeledValue(baseFormattedValue, baseLabel, baseProperties, 'baseValue', conditionalFormatting));

					const sparklineQueryResultData = renderInfo.data.getResult('sparkline');
					const showSparkline = this._shouldDisplaySparkline(sparklineQueryResultData);
					this._sparkLineNode.toggleClass('hidden', !showSparkline);
					if (showSparkline) {
						renderPromises.push(this._renderSparkLine(renderInfo, conditionalFormatting, baseProperties));
					} else {
						this._removeSparkLine();
					}

					return Promise.all(renderPromises);
				})
				.then(() => {
					const baseProperties = this._getBaseProperties();
					if (baseProperties.labelSize === 'auto') {
						this._scaleSections(this._getResizeDefinition());
					}
					return KpiView.inherited('render', this, arguments);
				});
		},

		_getQueryResultValue(queryResultData, rowIndex, dataItemIndex) {
			let value = null;
			const rowCount = queryResultData.getRowCount();
			if (rowIndex < rowCount) {
				const dataPointCount = queryResultData.getDataPointValueList()[rowIndex].pt.length;
				if (dataItemIndex < dataPointCount) {
					value = queryResultData.getValue(rowIndex, dataItemIndex).value;
				}
			}
			return value;
		},

		_getTargetValueString(targetProperties, formatSlot) {
			const targetFormattedValue = this._formatValue(formatSlot, this._targetValue);
			let targetString = '';
			if (targetProperties.showDistance) {
				const formattedTargetDistance = this._getFormattedTargetDistanceString(formatSlot, this._baseValue, this._targetValue);
				if (targetProperties.showValue) {
					targetString = StringResources.get('kpiTargetWithDistance', {
						value: targetFormattedValue,
						distance: formattedTargetDistance
					});
				} else {
					targetString = formattedTargetDistance;
				}
			} else {
				targetString = targetFormattedValue;
			}
			return targetString;
		},

		_renderLabeledValue: function(value, label, properties, sectionName, conditionalFormatting) {
			const viewName = `_${sectionName}View`;
			const nodeName = `_${sectionName}Node`;
			let valueStyle = '';
			if (properties.labelSize && properties.labelSize !== 'auto') {
				valueStyle = `font-size:${properties.labelSize}px;`;
			}
			if (conditionalFormatting && conditionalFormatting.color) {
				valueStyle += `color:${conditionalFormatting.color};`;
			} else if (properties.valueColor) {
				let hexColor = this._colorsService.getHexColorFromDashboardColorSet(properties.valueColor);
				if (hexColor !== 'transparent') {
					valueStyle += `color:${hexColor};`;
				}
			}
			const finalLabel = properties.label || label;
			let view = this[viewName];
			if (!view) {
				view = this[viewName] = this._kpiViewFactory.createLabeledValueView({
					el: this[nodeName][0],
					dashboardApi: this.dashboardApi,
					value: value,
					valueStyle: valueStyle,
					label: finalLabel,
					showLabel: properties.showLabel,
					conditionalFormatting: conditionalFormatting
				});
			} else {
				view.setValue(value);
				view.setValueStyle(valueStyle);
				view.setLabel(finalLabel);
				view.setShowLabel(properties.showLabel);
				view.setConditionalFormatting(conditionalFormatting);
			}
			return view.render();
		},

		_readyToRender: function() {
			return this.isMappingComplete() && !this.hasMissingFilters() && !this.hasUnavailableMetadataColumns();
		},

		_getResizeDefinition: function() {
			let sizingDefinition = {
				$node: this.$el,
				reservedSpace: 16, //Remove any padding between and around elements from size calculations.
				children: []
			};
			if (this._baseValueView){
				sizingDefinition.children.push({
					$node: this._baseValueView.getValueNode(),
					referenceHeight: 40,//Observed reference height in chrome given the rendering at the default size.
					minimumHeight: 22.8,//19 - Commented number is the desired minimum font size. Increased due to the line-height -> font-size reduction.
					text: true
				});
				if (this._baseValueView.hasShape()) {
					sizingDefinition.children[0].icon = {
						$node: this._baseValueView.getValueNode().find('svg'),
						paddingDivisor: 4
					};
				}
				if (this._baseValueView.getShowLabel()) {
					sizingDefinition.reservedSpace += 8;
					sizingDefinition.children.push({
						$node: this._baseValueView.getLabelNode(),
						referenceHeight: 14,
						minimumHeight: 9.6,//8
						maximumHeight: 29, //24  - Commented number is the desired maximum font size. Increased due to the line-height -> font-size reduction.
						text: true
					});
				}
				if (this._targetValueNode && !this._targetValueNode.hasClass('hidden') && this._targetValueView) {
					sizingDefinition.reservedSpace += 16;
					sizingDefinition.children.push({
						$node: this._targetValueView.getValueNode(),
						referenceHeight: 19,
						minimumHeight: 12,//10
						maximumHeight: 39, //32
						text: true
					});
					if (this._targetValueView.getShowLabel()) {
						sizingDefinition.reservedSpace += 8;
						sizingDefinition.children.push({
							$node: this._targetValueView.getLabelNode(),
							referenceHeight: 14,
							minimumHeight: 9.6,//8
							maximumHeight: 29, //24
							text: true
						});
					}
				}
				if (this._sparkLineNode && !this._sparkLineNode.hasClass('hidden') && this._sparkLineView) {
					sizingDefinition.reservedSpace += 16;
					sizingDefinition.children.push({
						$node: this._sparkLineNode,
						referenceHeight: 32,
						minimumHeight: 16,
						text: false
					});
				}
			}
			return sizingDefinition;
		},

		_scaleSections: function(sizingDefinition) {
			if (!sizingDefinition.children || !sizingDefinition.children.length) {
				return; //There are no child items, so there is nothing to size.
			}

			const LINE_HEIGHT_TO_FONT_SIZE = 1.2; //The font-size does not cover the full size needed to display that font. Reduce the font below the line-height to prevent font overflow.

			let padding = 0;
			if (sizingDefinition.reservedSpace) {
				padding += sizingDefinition.reservedSpace;
			}

			sizingDefinition.$node.css('overflow', 'hidden');
			const existingHeight = sizingDefinition.$node.height() - padding;
			let referenceHeight = 0;
			sizingDefinition.children.forEach(child => {
				referenceHeight += child.referenceHeight;
			});

			function setSizes(definition, size) {
				let sizeCapped = false;
				if (definition.minimumHeight) {
					size = Math.max(Math.floor(size), definition.minimumHeight);
				}
				if (definition.maximumHeight) {
					size = Math.min(size, definition.maximumHeight);
					sizeCapped = size === definition.maximumHeight;
				}
				let height = size;
				definition.$node.css('height', height+'px');
				const fontSize = size/LINE_HEIGHT_TO_FONT_SIZE;
				if (definition.text) {
					definition.$node.css({ 'font-size': fontSize+'px', 'line-height': size+'px' , 'flex-basis': size+'px' });
				}
				if (definition.icon) {
					let iconSize = fontSize/2;
					let iconCss = {
						width: iconSize+'px',
						height: iconSize+'px'
					};
					if (definition.icon.paddingDivisor) {
						iconCss['padding-left'] = (iconSize/definition.icon.paddingDivisor)+'px';
					}
					definition.icon.$node.css(iconCss);
				}
				return sizeCapped;
			}

			let someItemsCapped = false;
			let adjustedDefinition = {
				$node: sizingDefinition.$node,
				reservedSpace: sizingDefinition.reservedSpace,
				children: []
			};
			sizingDefinition.children.forEach(child => {
				const percentOfFullHeight = child.referenceHeight/referenceHeight;
				let childSize = percentOfFullHeight * existingHeight;
				let childSizeCapped = setSizes(child, childSize);
				if (child.text) {
					const boundingWidth = child.$node.width();
					let actualWidth = child.$node[0].scrollWidth;
					if (actualWidth > Math.round(boundingWidth)) {
						actualWidth += 8; //scrollWidth is rounded, plus reserve a couple pixels around the text to prevent some infrequent scrollbars, some of which can be caused by the selection chrome
						if (childSizeCapped) {
							childSize = child.maximumHeight;
						}
						childSize = childSize * boundingWidth / actualWidth;
						childSizeCapped = setSizes(child, childSize);
					}
				}
				someItemsCapped = someItemsCapped || childSizeCapped;
				if (!childSizeCapped) {
					adjustedDefinition.children.push(child);
				} else {
					adjustedDefinition.reservedSpace += child.maximumHeight;
				}
			});

			sizingDefinition.$node.css('overflow', '');
			if (someItemsCapped) { //Some items were capped. Resize the other items.
				this._scaleSections(adjustedDefinition);
			}
		},

		/**
		 * Return text formatted value for field
		 *
		 * @param slot, slotAPI
		 * @param v The raw fact value.
		 * @returns text formatted data value
		 */
		_formatValue: function(slot, v) {
			const formatSpec = this._getFormatForSlot(slot);
			v = Formatter.format(v, formatSpec);
			return v;
		},

		_getFormatForSlot(slot) {
			const dataItem = this._getDataItem(slot);
			const format = dataItem && dataItem.getFormat();
			return format || {};
		},

		_getFormattedTargetDistanceString(slot, baseValue, targetValue) {
			if (targetValue === 0) {
				return StringResources.get('kpiDistanceUndefined');
			}
			const targetDistance = (baseValue-targetValue)/Math.abs(targetValue);
			const formatSpec = {
				convertPercent: true,
				maximumFractionDigits: 2,
				minimumFractionDigits: 0,
				type: 'percent',
				locale: slot.locale
			};
			let distanceString = Formatter.format(targetDistance, formatSpec);
			if (targetDistance>0) {
				distanceString = '+' + distanceString;
			}
			return distanceString;
		},

		_getBaseProperties: function() {
			return {
				labelSize: this._content.getPropertyValue('baseValueSize'),
				showLabel: this._content.getPropertyValue('showItemLabel') === false ? false : true,
				label: this._content.getPropertyValue('baseValueLabel'),
				valueColor: this._content.getPropertyValue('elementColor')
			};
		},

		_getTargetProperties: function() {
			return {
				labelSize: this._content.getPropertyValue('targetValueSize'),
				showLabel: this._content.getPropertyValue('showTargetLabel') === false ? false : true,
				showValue: this._content.getPropertyValue('showTargetValue') === false ? false : true,
				showDistance: this._content.getPropertyValue('showTargetDistance') === false ? false : true,
				label: this._content.getPropertyValue('targetValueLabel'),
				manualValue: this._content.getPropertyValue('targetValue')
			};
		},

		_getTimeProperties: function() {
			return {
				showSparkline: this._content.getPropertyValue('showSparkline') === false ? false : true
			};
		},

		_getTextAlignment: function() {
			return this._content.getPropertyValue('textAlignment');
		},

		_getBaseSlot: function() {
			return this._visualizationAPI.getSlots().getSlot(this.BASE_SLOT_ID);
		},

		_getTargetSlot: function() {
			return this._visualizationAPI.getSlots().getSlot(this.TARGET_SLOT_ID);
		},

		_getSparklineTimeSlot: function() {
			return this._visualizationAPI.getSlots().getSlot(this.SPARKLINE_TIME_SLOT_ID);
		},

		_getSparklineActualSlot: function() {
			return this._visualizationAPI.getSlots().getSlot(this.SPARKLINE_ACTUAL_SLOT_ID);
		},

		_getDataItem: function(slot, dataItemIndex = 0) {
			return slot.getDataItemList()[dataItemIndex];
		},

		_isSlotMapped: function(slot) {
			const dataItems = slot && slot.getDataItemList() || [];
			return dataItems.length > 0;
		},

		animate: function() {
			if (this.resizing) {
				this.visModel.renderCompleteBeforeAnimation(); // Rendering is complete, no animation
			} else {
				KpiView.inherited('animate', this, arguments);
			}
		},

		resize: function() {
			// intentionally override VisView behavior.
			if (this.isMappingComplete()) {
				//TODO: Update which sections are shown based on size.
				const baseProperties = this._getBaseProperties();
				if (baseProperties.labelSize === 'auto') {
					this._scaleSections(this._getResizeDefinition());
				}
			} else {
				KpiView.inherited('resize', this, arguments);
			}
		},

		//[livewidget-cleanup] todo: this function is moved to KpiPropertyCallbacks
		onChangeValueSizeProperty: function(propertyName, propertyValue) {
			let props = {};
			if (propertyValue === 'auto') {
				props['baseValueSize'] = 'auto';
				props['targetValueSize'] = 'auto';
			} else {
				props['baseValueSize'] = this._content.getPropertyValue('baseValueSize');
				props['targetValueSize'] = this._content.getPropertyValue('targetValueSize');
				if (props['baseValueSize'] === 'auto') {
					props['baseValueSize'] = '32';
					props['targetValueSize'] = '16';
				}
				props[propertyName] = propertyValue;
			}
			const transactionId = _.uniqueId('kpi_font_size');

			for (let property in props) {
				this.ownerWidget.onPropertyUpdate({
					'category': property,
					'item': props[property],
					'transactionId': transactionId
				});
			}
		},

		getDecoratorAPI: function() {
			const indexedView = [this._baseValueView, this._targetValueView];
			return {
				decorateItem: (itemIndex) => {
					if (indexedView[itemIndex]) {
						indexedView[itemIndex].setShowLabelTranslationIcon(true);
					}
				},
				decoratePoint: () => null,
				decorate: () => null,
				clearItemDecoration: () =>{
					indexedView.forEach(view => {
						if (view) {
							view.setShowLabelTranslationIcon(false);
						}
					});
				},
				updateDecorations: () => {
					indexedView.forEach(view => {
						if (view) {
							view.render();
						}
					});
				}
			};
		},

		getBaseValue: function() {
			return this._baseValue;
		},

		getTargetValue: function() {
			return this._targetValue;
		},

		_removeSparkLine: function() {
			if (this._sparkLineView) {
				this._sparkLineView.destroy();
				this._sparkLineView = null;
			}
		},

		_shouldDisplaySparkline: function(sparklineQueryResultData) {
			if (!this._isSlotMapped(this._getSparklineActualSlot())) {
				return false;
			}

			if (!this._isSlotMapped(this._getSparklineTimeSlot())) {
				return false;
			}

			if (!this._getTimeProperties().showSparkline) {
				return false;
			}

			if (!this._hasEnoughDataToRenderSparkline(sparklineQueryResultData)) {
				return false;
			}

			return true;
		},

		_hasEnoughDataToRenderSparkline(sparklineQueryResultData) {
			if (!sparklineQueryResultData) {
				return false;
			}

			const dataPointValueList = sparklineQueryResultData.getDataPointValueList();
			const dataPointValueListLength = dataPointValueList.length;
			// Need at least 2 data point to draw the sparkline
			if (!dataPointValueList || dataPointValueListLength < 2) {
				this._addNotRenderingSparklineInfo();
				return false;
			}

			// Make sure we have at least 2 non NULL values
			let nonNullCount = 0;
			for (let i=0; i < dataPointValueListLength; i++) {
				if (sparklineQueryResultData.getValue(i, 0).value !== null) {
					nonNullCount++;
				}

				if (nonNullCount > 1) {
					break;
				}
			}

			if (nonNullCount < 2) {
				this._addNotRenderingSparklineInfo();
				return false;
			}

			return true;
		},

		_addNotRenderingSparklineInfo() {
			if (this.infoIndicator) {
				this.infoIndicator.addInfo([{
					id: 'data_notifications',
					label: StringResources.get('visualizationNotifications'),
					items: [{
						id: 'sparkLineInfoLabel',
						label: StringResources.get('kpiUnableDrawSparklineLabel'),
						isSubMessage: false
					}, {
						id: 'sparkLineInfo',
						label: StringResources.get('kpiUnableDrawSparklineInfo'),
						isSubMessage: true
					}]
				}]);
			}
		},

		_renderSparkLine(renderInfo, conditionalFormatting, baseProperties) {
			let sparkLineColor = '';
			if (conditionalFormatting && conditionalFormatting.color) {
				sparkLineColor = conditionalFormatting.color;
			} else if (baseProperties.valueColor) {
				let hexColor = this._colorsService.getHexColorFromDashboardColorSet(baseProperties.valueColor);
				if (hexColor !== 'transparent') {
					sparkLineColor = hexColor;
				}
			}

			if (this._sparkLineView) {
				if (renderInfo.refresh && renderInfo.refresh.data) {
					// Query data, or color has changed, need to recreate the sparkline
					this._removeSparkLine();
				} else if (this.resizing) {
					this._sparkLineView.resize();
				}
			}
			const sparklineColorChanged = sparkLineColor !== this._cachedSparkLineColor;
			this._updateCachedSparkLineColor(sparkLineColor);

			if (!this._sparkLineView) {
				const sparklineQueryResults = new QueryResults();
				sparklineQueryResults.addResult(renderInfo.data.getResult('sparkline'));
				this._sparkLineView = this._createSparkLine(sparklineQueryResults.getAPI());
				this._sparkLineView.render();
			} else if (sparklineColorChanged) { //If we already have a sparkline and there was a color change. Update the color property of the sparkline.
				this._sparkLineView.widget.updateVisProperties(this._getSparklineColorProperty());
			}
			return Promise.resolve();
		},

		_getSparklineContainer() {
			return this.$el.find('.kpi-widget-sparkline');
		},

		_addSlotDef(spec, slotName, slot) {
			const slotDef = {
				'name': slotName,
				'dataItems': []
			};
			slot.getDataItemList().forEach((dataItem) => {
				let dataItemDef = {
					id: dataItem.getId(),
					itemId: dataItem.getColumnId()
				};
				if (dataItem.getSort()) {
					dataItemDef.selection = [{
						operation: 'order',
						sort: dataItem.getSort()
					}];
				}
				spec.data.dataViews[0].dataItems.push(dataItemDef);
				slotDef.dataItems.push(dataItem.getId());
			});
			spec.slotmapping.slots.push(slotDef);
		},

		_getSparklineColorProperty: function() {
			return {
				id: 'colors.series',
				value: this._cachedSparkLineColor
			};
		},

		_updateCachedSparkLineColor: function(color) {
			this._cachedSparkLineColor = color;
			const container = this._getSparklineContainer();
			if (this._cachedSparkLineColor) {
				container.removeClass('defaultColors');
			} else {
				container.addClass('defaultColors'); //When no explicit color is set, use CSS to override the line color to match the default value color.
			}
		},

		_buildSparklineSpec() {
			const spec = JSON.parse(KPISparklineSpec);

			if (this._cachedSparkLineColor) {
				spec.properties.push(this._getSparklineColorProperty());
			}

			spec.data = {
				'dataViews': [{
					modelRef: this._visualizationAPI.getDataSource().getId(),
					dataItems: []
				}]
			};
			spec.slotmapping.slots = [];
			this._addSlotDef(spec, 'categories', this._getSparklineTimeSlot());
			this._addSlotDef(spec, 'values', this._getSparklineActualSlot());

			const localFilters = this._visualizationAPI.getLocalFilters();
			spec.localFilters = localFilters.getFilterList();

			return spec;
		},

		_createSparkLine(queryResults) {
			const spec = this._buildSparklineSpec();
			const container = this._getSparklineContainer();
			const options = {
				enableHover: true,
				enableClick: false,
				enablePanAndZoom: false,
				tabNavigation: false,
				runtimeEnv: {
					dashboardApi: this.dashboardApi
				},
				node: container[0],
				spec: spec,
				queryResults: queryResults,
				managesOwnQueries: true
			};

			return this._kpiViewFactory.createSparklineView(options);
		},

		_isNewConditionalFormatFeature () {
			return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'condFormat', 'disabled');
		}
	});

	return KpiView;
});
